import type { VoxelMaterial } from '@/voxelTypes'

export type SdfBlobOptions = {
  samplesPerVoxel: number
  roundRadius: number
  blendRadius: number
  surfaceOffset: number
  padding: number
  tightBounds: boolean
  maxVertices: number
  /**
   * Legacy planner: the whole-group field size (nodes) from which splitting into separate pieces is
   * considered (default `SDF_BLOB_SPLIT_MIN_FIELD_NODES`; the split still has to save
   * `SDF_BLOB_SPLIT_MIN_WORK_SAVINGS_RATIO`). Lower it when fewer samples per voxel make a scene of
   * several pieces fall under the default, so a stroke re-meshes only the piece it touches.
   */
  splitMinFieldNodes?: number
}

export const DEFAULT_SDF_BLOB_OPTIONS: SdfBlobOptions = Object.freeze({
  samplesPerVoxel: 5,
  roundRadius: 0.2,
  blendRadius: 0.85,
  surfaceOffset: 0,
  padding: 1,
  tightBounds: true,
  maxVertices: 3_500_000,
})

export const CLOUD_SDF_BLOB_OPTIONS: SdfBlobOptions = Object.freeze({
  samplesPerVoxel: 3,
  roundRadius: 0.75,
  blendRadius: 1.2,
  surfaceOffset: 0.12,
  padding: 2,
  tightBounds: true,
  maxVertices: 3_500_000,
})

export const CLOUD_ACCRETE_RADIUS = 1.35
export const CLOUD_SEED_RADIUS = 0.5
export const CLOUD_ACCRETE_LUMPINESS = 0.72

type BlobPhongMaterialSettings = {
  shininess: number
  specular: number
}

type BlobStandardMaterialSettings = {
  roughness: number
  metalness: number
  emissive: number
  emissiveIntensity: number
  envMapIntensity: number
}

type BlobFallbackMaterialSettings = {
  roughness: number
  metalness: number
}

export const BLOB_MOLTEN_SILVER_COLOR = 0xe8eef3
export const BLOB_MOLTEN_SILVER_MATERIAL: Extract<VoxelMaterial, 'metallic'> = 'metallic'

export const BLOB_PHONG_MATERIAL_SETTINGS: Readonly<Record<Extract<VoxelMaterial, 'soft' | 'glossy'>, BlobPhongMaterialSettings>> = Object.freeze({
  soft: { shininess: 28, specular: 0x40525a },
  glossy: { shininess: 120, specular: 0xffffff },
})

export const BLOB_SHINY_MATERIAL_SETTINGS: BlobStandardMaterialSettings = Object.freeze({
  roughness: 0,
  metalness: 0.2,
  emissive: 0x000000,
  emissiveIntensity: 0,
  envMapIntensity: 1,
})

export const BLOB_METALLIC_MATERIAL_SETTINGS: BlobStandardMaterialSettings = Object.freeze({
  roughness: 0.12,
  metalness: 0.9,
  emissive: 0x000000,
  emissiveIntensity: 0,
  envMapIntensity: 1.8,
})

export const BLOB_FALLBACK_MATERIAL_SETTINGS: Readonly<Record<VoxelMaterial, BlobFallbackMaterialSettings>> = Object.freeze({
  soft: { roughness: 0.48, metalness: 0 },
  glossy: { roughness: 0.22, metalness: 0 },
  shiny: { roughness: 0, metalness: 0.2 },
  metallic: { roughness: 0.12, metalness: 0.9 },
})

export const SDF_BLOB_MIN_SAMPLES_PER_VOXEL = 1
export const SDF_BLOB_MAX_FIELD_BUFFER_BYTES = 64 * 1024 * 1024
export const SDF_BLOB_FIELD_BYTES_PER_NODE = Uint32Array.BYTES_PER_ELEMENT
export const SDF_BLOB_SPLIT_MIN_FIELD_NODES = 1_000_000
export const SDF_BLOB_SPLIT_MAX_BOUNDS_FILL_RATIO = 0.35
export const SDF_BLOB_SPLIT_MIN_WORK_SAVINGS_RATIO = 0.35
export const SDF_BLOB_SPLIT_COMPONENT_OVERHEAD_WORK_UNITS = 50_000
export const SDF_BLOB_VERTEX_BUDGET_MIN = 6_000
export const SDF_BLOB_VERTEX_BUDGET_SURFACE_FACTOR = 72
export const SDF_BLOB_JFA_WORKGROUP_SIZE = 4
export const SDF_BLOB_MARCH_WORKGROUP_SIZE = 4
export const SDF_BLOB_COORD_BITS = 10
export const SDF_BLOB_COORD_LIMIT = 1 << SDF_BLOB_COORD_BITS
export const SDF_BLOB_INVALID_SEED = 0xffffffff
export const SDF_BLOB_VERTEX_STRIDE_FLOATS = 9
export const SDF_BLOB_VERTEX_STRIDE_BYTES =
  Float32Array.BYTES_PER_ELEMENT * SDF_BLOB_VERTEX_STRIDE_FLOATS

export const GPU_BUFFER_USAGE =
  typeof GPUBufferUsage !== 'undefined'
    ? GPUBufferUsage
    : ({
      MAP_READ: 0x0001,
      MAP_WRITE: 0x0002,
      COPY_DST: 0x0008,
      COPY_SRC: 0x0004,
      STORAGE: 0x0080,
      VERTEX: 0x0020,
      UNIFORM: 0x0040,
      INDIRECT: 0x0100,
    } satisfies Record<string, number>)

/** Blob radius caps (materials-perf-plan Q5): 12 on desktop, 8 on touch devices until D3 is measured. */
export const BLOB_MAX_BRUSH_RADIUS_DESKTOP = 12
export const BLOB_MAX_BRUSH_RADIUS_TOUCH = 8

export function resolveBlobMaxBrushRadius(maxTouchPoints: number | undefined): number {
  return (maxTouchPoints ?? 0) > 0 ? BLOB_MAX_BRUSH_RADIUS_TOUCH : BLOB_MAX_BRUSH_RADIUS_DESKTOP
}

/** The blob cap for this device, read once at load (`navigator.maxTouchPoints`). */
export function blobMaxBrushRadius(): number {
  const nav = typeof navigator === 'undefined' ? undefined : navigator
  return resolveBlobMaxBrushRadius(nav?.maxTouchPoints)
}
